import { useParams } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import { useMemo, useState, useEffect, useCallback } from 'react';

import { Box } from '@mui/system';
import { Tab, Card, Grid, Tabs, alpha, Stack, CardHeader, Typography, CardContent } from '@mui/material';

import { useBoolean } from 'src/hooks/use-boolean';

import { parseHighlightTerms } from 'src/utils/highlight-terms';

import { useTranslate } from 'src/locales';
import { rootApi } from 'src/services/rootApi';
import { useAppDispatch } from 'src/store/store';
import { DocumentType } from 'src/services/files/file.types';
import { ListEntityType } from 'src/services/lists/lists.types';
import { SigningOriginType } from 'src/services/signing/signing.types';
import { OrganizationCandidateType } from 'src/services/candidates/candidates.types';
import { exportOrgCandidateProfile } from 'src/services/candidates/candidates.utils';
import { useGetCandidateDocumentsQuery, useGetOrganizationCandidateQuery, useGetOrgCandidateActivitiesQuery } from 'src/services/candidates/candidates.service';

import CandidateResumeView from 'src/components/candidates/candidate-resume-view';

import ActivityFeed from 'src/sections/activity/card/activity-feed';
import CandidateJobsList from 'src/sections/candidates/jobs/jobs-table-view';
import ProfileUser from 'src/sections/profile/profile-sections/profile-user';
import SendForSigningModal from 'src/sections/signing/send-for-signing-modal';
import SigningDocumentsList from 'src/sections/signing/signing-documents-list';
import { EntityListsCard } from 'src/sections/lists/components/entity-lists-card';
import AddCandidateJobModal from 'src/sections/jobs/modals/add-candidate-job-modal';
import CandidateProfileEditor from 'src/sections/profile/profile-sections/profile-editor';
import { CandidateDetailSection } from 'src/sections/candidates/detail/sections/detail-section';
import CandidateDocumentsView from 'src/sections/candidates/detail/sections/candidate-documents';
import { LinkedContactsCard } from 'src/sections/candidates/detail/sections/linked-contacts-card';
import { CandidateLabelsCard } from 'src/sections/candidates/detail/sections/candidate-labels-card';
import { CandidateContactCard } from 'src/sections/candidates/detail/sections/candidate-contact-card';
import OrgCandidateProfileImporterDialog from 'src/sections/candidates/modal/candidate-profile-importer';

// ----------------------------------------------------------------------

enum TabValue {
  PROFILE = 'profile',
  RESUME = 'resume',
  JOBS = 'jobs',
  DETAIL = 'detail',
  DOCUMENTS = 'documents',
}

enum ProfileTabValue {
  CRM_PROFILE = 'crm_profile',
  LIVE_PROFILE = 'live_profile'
}

interface Props {
  overrideCandidateId?: string;
}

export default function CandidateDetail({ overrideCandidateId }: Props) {

  const [selectedTab, onSelectTab] = useState<TabValue>();
  const [selectedProfileTab, onSelectProfileTab] = useState<ProfileTabValue>(ProfileTabValue.CRM_PROFILE);

  const { t } = useTranslate();

  const { candidate_id: candidateIdParam } = useParams();

  const dispatch = useAppDispatch();

  const importProfileControl = useBoolean();

  const addCandidateJobControl = useBoolean();

  const sendForSigning = useBoolean();

  const [searchParams, setSearchParams] = useSearchParams();

  const candidateId = useMemo(() => {
    if (overrideCandidateId) return overrideCandidateId;

    return candidateIdParam;
  }, [overrideCandidateId, candidateIdParam]);

  const [activitiesPage, setActivitiesPage] = useState({ page: 1, per_page: 10 });

  const { currentData: orgCandidate, isFetching: isLoading } = useGetOrganizationCandidateQuery(
    candidateId as string,
    {
      skip: !candidateId,
    }
  );

  const { data: activities } = useGetOrgCandidateActivitiesQuery({
    candidate_id: candidateId as string,
    page: activitiesPage.page,
    perPage: activitiesPage.per_page,
  }, { skip: !candidateId });

  const { data: candidateDocs, isLoading: candidateDocsLoading } = useGetCandidateDocumentsQuery(
    { candidate_id: candidateId as string, params: { page: 1, per_page: 50 } },
    { skip: !candidateId }
  );

  // Resume tab (spec item 18, JT1-1537): the newest RESUME document (for the PDF fallback) plus the
  // parsed resume_text on the org candidate. The tab appears only when at least one of them exists.
  const { data: resumeDocs } = useGetCandidateDocumentsQuery(
    { candidate_id: candidateId as string, params: { page: 1, per_page: 1, type: DocumentType.RESUME } },
    { skip: !candidateId }
  );
  const resumeDoc = resumeDocs?.results?.[0];
  const hasResume = Boolean(orgCandidate?.resume_text) || Boolean(resumeDoc);

  // Highlight resume search terms when the profile was opened from a search (?q=…).
  const highlightTerms = useMemo(() => parseHighlightTerms(searchParams.get('q')), [searchParams]);

  const signingDocuments = useMemo(
    () =>
      (candidateDocs?.results ?? []).map((d: any) => ({
        file_id: d.document?.file_id ?? d.document?.id ?? d.id,
        name: d.document?.name ?? d.document?.filename ?? 'Document',
        mime: d.document?.mime,
      })),
    [candidateDocs]
  );

  const signingDefaultSigners = useMemo(() => {
    const profile = orgCandidate?.candidate_profile;
    if (!profile?.email) return undefined;
    return [
      {
        first_name: profile.first_name ?? '',
        last_name: profile.last_name ?? '',
        email: profile.email,
      },
    ];
  }, [orgCandidate]);

  useEffect(() => {
    // eslint-disable-next-line no-useless-return
    if (!selectedTab) return;

    // Embedded in the record overlay (JT1-1529): the tab is local state. Writing it to the URL would
    // replace the whole query string and wipe the co-owned ?preview params, closing the overlay — the
    // same clobber fixed in candidate-list-view. Only sync the tab on the standalone profile page.
    if (overrideCandidateId) return;

    // MERGE the tab into the existing query rather than replacing it, so co-owned params survive —
    // e.g. the ?q= search term the Resume tab highlights (JT1-1537). A bare object replace wiped it.
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('tab', selectedTab);
        return next;
      },
      { replace: true }
    );

  }, [selectedTab, setSearchParams, overrideCandidateId]);

  useEffect(() => {
    // eslint-disable-next-line no-useless-return
    if (selectedTab) return;

    let defaultTab: string = TabValue.DETAIL;

    const existingTab = searchParams.get('tab');
    if (existingTab && Object.values(TabValue).includes(existingTab as TabValue)) {
      defaultTab = existingTab;
    }

    onSelectTab(defaultTab as TabValue);

  }, [selectedTab, searchParams]);

  const onProfileUpdate = useCallback(() => {
    dispatch(rootApi.util.invalidateTags([{ type: 'OrgCandidates', id: candidateId as string }]));

  }, [dispatch, candidateId]);

  const onImportProfile = useCallback(() => {
    importProfileControl.onTrue();
  }, [importProfileControl]);

  const onProfileImportSuccess = useCallback(() => {
    importProfileControl.onFalse();
  }, [importProfileControl]);

  const openAddCandidateJobModal = useCallback(() => {
    addCandidateJobControl.onTrue();
  }, [addCandidateJobControl]);

  const closeAddCandidateJobModal = useCallback(() => {
    addCandidateJobControl.onFalse();
  }, [addCandidateJobControl]);

  return (
    <Grid container gap={3}>
      <Grid item xs={12}>
        <ProfileUser
          profile={orgCandidate?.candidate_profile}
          isLoading={isLoading}
          mode="view"
          fallbackLocation={orgCandidate?.synced_profile?.location}
          commsEntityId={candidateId as string}
          showAvailabilityStatus={orgCandidate?.type !== OrganizationCandidateType.UNLINKED}
          onImportProfile={onImportProfile}
          onAddToJob={!overrideCandidateId ? openAddCandidateJobModal : undefined}
          onExportProfile={!overrideCandidateId ? () => exportOrgCandidateProfile(candidateId as string) : undefined}
          onSendForSigning={sendForSigning.onTrue}
        />
      </Grid>

      <Grid item xs={12}>
        <Tabs
          value={selectedTab}
          onChange={(event, newValue) => onSelectTab(newValue)}
          sx={{
            px: 2.5,
            mb: { xs: 1, sm: 'unset' },
            boxShadow: (elTheme) => `inset 0 -2px 0 0 ${alpha(elTheme.palette.grey[500], 0.08)}`,
          }}
        >
          <Tab
            key={TabValue.DETAIL}
            iconPosition="end"
            value={TabValue.DETAIL}
            label={
              <Typography
                variant="subtitle2"
                sx={{ textTransform: 'capitalize', fontWeight: 'bold' }}
              >
                {t('candidates.detail.tabs.detail')}
              </Typography>
            }
          />

          <Tab
            key={TabValue.PROFILE}
            iconPosition="end"
            value={TabValue.PROFILE}
            label={
              <Typography
                variant="subtitle2"
                sx={{ textTransform: 'capitalize', fontWeight: 'bold' }}
              >
                {t('candidates.detail.tabs.profile')}
              </Typography>
            }
          />

          {hasResume && (
            <Tab
              key={TabValue.RESUME}
              iconPosition="end"
              value={TabValue.RESUME}
              label={
                <Typography
                  variant="subtitle2"
                  sx={{ textTransform: 'capitalize', fontWeight: 'bold' }}
                >
                  {t('candidates.detail.tabs.resume')}
                </Typography>
              }
            />
          )}

          <Tab
            key={TabValue.JOBS}
            iconPosition="end"
            value={TabValue.JOBS}
            label={
              <Typography
                variant="subtitle2"
                sx={{ textTransform: 'capitalize', fontWeight: 'bold' }}
              >
                {t('candidates.detail.tabs.jobs')}
              </Typography>
            }
          />

          <Tab
            key={TabValue.DOCUMENTS}
            iconPosition="end"
            value={TabValue.DOCUMENTS}
            label={
              <Typography
                variant="subtitle2"
                sx={{ textTransform: 'capitalize', fontWeight: 'bold' }}
              >
                {t('candidates.detail.tabs.documents')}
              </Typography>
            }
          />

        </Tabs>
      </Grid>


      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          {selectedTab === TabValue.DETAIL && (
            <Stack spacing={2}>
              <CandidateDetailSection candidateId={candidateId as string} />
              <Card>
                <CardHeader title={t('admin-connections.detail.activities')} />
                <CardContent>
                  <ActivityFeed
                    activities={activities?.results ?? []}
                    page={activitiesPage.page}
                    perPage={activitiesPage.per_page}
                    onPageChange={(_e, newPage) => setActivitiesPage({ ...activitiesPage, page: newPage })}
                    totalItems={activities?.count ?? 0}
                  />
                </CardContent>
              </Card>
            </Stack>
          )}

          {selectedTab === 'profile' && (
            <>
              {
                orgCandidate?.type === OrganizationCandidateType.LINKED && (
                  <Box mb={2}>
                    <Tabs
                      value={selectedProfileTab}
                      onChange={(event, newValue) => onSelectProfileTab(newValue)}
                      sx={{
                        px: 2.5,
                        mb: { xs: 1, sm: 'unset' },
                        boxShadow: (elTheme) => `inset 0 -2px 0 0 ${alpha(elTheme.palette.grey[500], 0.08)}`,
                      }}
                    >
                      <Tab
                        key={ProfileTabValue.CRM_PROFILE}
                        iconPosition="end"
                        value={ProfileTabValue.CRM_PROFILE}
                        label={
                          <Typography
                            variant="subtitle2"
                            sx={{ textTransform: 'capitalize', fontWeight: 'bold' }}
                          >
                            {t('candidates.detail.tabs.crm_profile')}
                          </Typography>
                        }
                      />

                      <Tab
                        key={ProfileTabValue.LIVE_PROFILE}
                        iconPosition="end"
                        value={ProfileTabValue.LIVE_PROFILE}
                        label={
                          <Typography
                            variant="subtitle2"
                            sx={{ textTransform: 'capitalize', fontWeight: 'bold' }}
                          >
                            {t('candidates.detail.tabs.live_profile')}
                          </Typography>
                        }
                      />
                    </Tabs>
                  </Box>
                )
              }

              {
                selectedProfileTab === ProfileTabValue.CRM_PROFILE && (
                  <CandidateProfileEditor
                    onUpdate={onProfileUpdate}
                    profile={orgCandidate?.candidate_profile}
                    isLoading={isLoading}
                    mode="edit"
                    commsEntityId={candidateId as string}
                  />
                )
              }

              {
                selectedProfileTab === ProfileTabValue.LIVE_PROFILE && (
                  <CandidateProfileEditor
                    onUpdate={() => { }}
                    profile={orgCandidate?.synced_profile}
                    isLoading={isLoading}
                    mode="view"
                    commsEntityId={candidateId as string}
                  />
                )
              }
            </>
          )}

          {selectedTab === TabValue.RESUME && (
            <Card>
              <CardContent>
                {/* Default to the supplied formatted PDF; CandidateResumeView falls back to parsed text
                    when there is no document, and still forces text when highlight terms exist (JT1-1537 QA). */}
                <CandidateResumeView
                  resumeText={orgCandidate?.resume_text}
                  resumeDocUrl={resumeDoc?.document?.public_path}
                  highlightTerms={highlightTerms}
                  prefer="document"
                />
              </CardContent>
            </Card>
          )}

          {selectedTab === 'jobs' && (
            <CandidateJobsList candidateId={candidateId as string} />
          )}

          {selectedTab === 'documents' && (
            <Stack spacing={3}>
              <CandidateDocumentsView candidateId={candidateId as string} />
              <SigningDocumentsList originType={SigningOriginType.CANDIDATE} originId={candidateId as string} />
            </Stack>
          )}
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Stack direction="column" spacing={2}>
            {/* Candidate's contact information */}
            <CandidateContactCard candidateId={candidateId as string} />

            {/* Candidate's linked contacts */}
            {orgCandidate && (
              <>
                <LinkedContactsCard candidateId={orgCandidate.id} />
                <CandidateLabelsCard candidateId={orgCandidate.id} />
                {/* JT1-1276 Phase 5: lists this candidate belongs to (below Linked) */}
                <EntityListsCard
                  entityType={ListEntityType.CANDIDATE}
                  entityId={orgCandidate.id}
                />
              </>
            )}
          </Stack>
        </Grid>
      </Grid>

      {importProfileControl.value && (
        <OrgCandidateProfileImporterDialog
          candidateId={candidateId as string}
          open={importProfileControl.value}
          onClose={importProfileControl.onFalse}
          onSuccess={onProfileImportSuccess}
        />
      )}

      <AddCandidateJobModal open={addCandidateJobControl.value} onClose={closeAddCandidateJobModal} candidateId={candidateId as string} />

      {sendForSigning.value && (
        <SendForSigningModal
          open={sendForSigning.value}
          onClose={sendForSigning.onFalse}
          originType={SigningOriginType.CANDIDATE}
          originId={candidateId as string}
          documents={signingDocuments}
          documentsLoading={candidateDocsLoading}
          defaultSigners={signingDefaultSigners}
        />
      )}
    </Grid>
  );
}
